/**
 * Phase 66 Plan 02 — Vite SPA entry.
 *
 * CRITICAL ORDERING (RESEARCH §Pitfall 5):
 *   1. Hash-route migration shim   (rewrites URL synchronously)
 *   2. createRoot(...).render(...) (router parses rewritten URL)
 *
 * The shim MUST run BEFORE the RouterProvider mounts; after-the-fact
 * history.replaceState() leaves the router resolved against `/`,
 * producing a flash of dashboard then a 404 / mismatched route. This
 * is verified mechanically by the awk gate in 66-02-PLAN <verify>.
 */

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { I18nextProvider } from "react-i18next";
import { Toaster } from "sonner";

import i18n from "./i18n/config";
import { AuthProvider } from "./auth/AuthProvider";
import { router } from "./router";
import { useFleetWebSocket } from "./hooks/useFleetWebSocket";
import "./styles/globals.css";

// 1) One-time hash-route migration shim — synchronous, before router init.
//    Phase 65 hash URLs (e.g., http://host/#/machines) → Phase 66 path URLs.
//    nginx SPA fallback (try_files $uri $uri/ /index.html) makes deep-link
//    reloads work transparently, so the shim is the only piece needed at the
//    client to handle existing bookmarks.
if (typeof window !== "undefined" && window.location.hash.startsWith("#/")) {
  const target = window.location.hash.slice(1);
  window.history.replaceState(null, "", target);
}

// 2) React Query client. retry: false — TanStack Query global behaviour
//    aligned with js/api.js: error surfaces immediately, no automatic retry.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: false, staleTime: 10_000 },
    mutations: { retry: false },
  },
});

const rootEl = document.getElementById("app");
if (!rootEl) {
  throw new Error("#app root element missing — index.html is out of sync with main.tsx");
}

// DEV-ONLY: MSW worker for preview without backend.
// Tree-shaken out of production build by Vite (import.meta.env.DEV guard).
async function startMocks() {
  if (import.meta.env.DEV) {
    const { worker } = await import("./mocks/browser");
    await worker.start({ onUnhandledRequest: "bypass", quiet: false });
  }
}

await startMocks();

/**
 * Phase 67-05 — fleet WS bridge. Mounts useFleetWebSocket() once inside
 * QueryClientProvider + AuthProvider so the hook can read the JWT and
 * push state into the TanStack cache. Renders no DOM.
 */
function FleetWebSocketBridge() {
  useFleetWebSocket();
  return null;
}

createRoot(rootEl).render(
  <StrictMode>
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <FleetWebSocketBridge />
          {/* Plan 06 D-09 — CommandPalette MUST live inside RouterProvider
              tree: it uses useSearchParams (via useCircuitId → useMachines),
              which requires Router context. Mounted in AppShell instead.
              See AppShell.tsx — palette renders only when authenticated. */}
          <RouterProvider router={router} />
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </QueryClientProvider>
    </I18nextProvider>
  </StrictMode>,
);
